import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import {
  FileText,
  RefreshCw
} from 'lucide-react';

const API = `${(process.env.REACT_APP_BACKEND_URL || window.location.origin)}/api`;

const AuditLogsSection = () => {
  const [logs, setLogs] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadData = async () => {
    setLoading(true);
    try {
      const [logsRes, summaryRes] = await Promise.all([
        axios.get(`${API}/admin/audit-logs`, { params: { limit: 100 } }),
        axios.get(`${API}/admin/audit-logs/summary`)
      ]);
      setLogs(logsRes.data);
      setSummary(summaryRes.data);
    } catch (error) {
      console.error("Error loading audit logs:", error);
    }
    setLoading(false);
  };

  useEffect(() => { loadData(); }, []);

  const actionColors = {
    login: "blue",
    create: "emerald",
    update: "amber",
    delete: "red",
    view: "slate"
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-3xl p-6 shadow-lg border border-slate-100">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-xl bg-gradient-to-br from-slate-600 to-slate-700">
              <FileText className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-800">Audit Logs</h2>
              <p className="text-sm text-slate-500">Track all admin activities and changes</p>
            </div>
          </div>
          <motion.button
            onClick={loadData}
            className="px-4 py-2 rounded-xl bg-slate-100 text-slate-700 font-medium hover:bg-slate-200 transition-all flex items-center gap-2"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </motion.button>
        </div>
      </div>

      {/* Summary Stats */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="p-4 rounded-2xl bg-gradient-to-br from-slate-100 to-slate-200 border border-slate-300">
            <p className="text-sm text-slate-600">Total Logs</p>
            <p className="text-2xl font-bold text-slate-800">{summary.total_logs}</p>
          </div>
          {Object.entries(summary.by_action || {}).slice(0, 4).map(([action, count]) => {
            const color = actionColors[action] || "slate";
            return (
              <div key={action} className="p-4 rounded-2xl bg-slate-50 border border-slate-200">
                <p className="text-sm text-slate-600 capitalize">{action}</p>
                <p className="text-xl font-bold text-slate-800">{count}</p>
              </div>
            );
          })}
        </div>
      )}

      {/* Logs List */}
      <div className="bg-white rounded-3xl p-6 shadow-lg border border-slate-100">
        <h3 className="font-semibold text-slate-800 mb-4">Recent Activity</h3>
        
        {loading ? (
          <div className="text-center py-8 text-slate-500">Loading...</div>
        ) : logs.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-slate-100 flex items-center justify-center">
              <FileText className="w-10 h-10 text-slate-300" />
            </div>
            <p className="text-slate-600 font-medium">No audit logs yet</p>
          </div>
        ) : (
          <div className="space-y-2">
            {logs.map((log, index) => {
              const color = actionColors[log.action] || "slate";
              return (
                <motion.div
                  key={log.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.02 }}
                  className="p-3 rounded-xl bg-slate-50 border border-slate-200 hover:bg-slate-100 transition-all"
                >
                  <div className="flex items-center gap-3">
                    <span className={`px-2 py-1 rounded-lg bg-${color}-100 text-${color}-700 text-xs font-medium uppercase`}>
                      {log.action}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-slate-800">
                        <span className="font-medium">{log.user_name}</span>
                        <span className="text-slate-500"> • {log.entity_type}</span>
                      </p>
                      {log.details && <p className="text-xs text-slate-500 truncate">{log.details}</p>}
                    </div>
                    <span className="text-xs text-slate-400">
                      {new Date(log.created_at).toLocaleString()}
                    </span>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default AuditLogsSection;
